/**
 * Routing.
 *
 * The back office is behind `lazy()` as a single boundary rather than per screen. It is
 * roughly half the JavaScript in the application and is used by a handful of staff;
 * a visitor reading a case study should never download the products table. Splitting it
 * further would trade a smaller first admin paint for a spinner between every
 * back-office screen, which is the wrong way round for a tool people use all day.
 *
 * The public routes are eager. They are what somebody arriving from a search result
 * needs, and a lazy boundary there buys a flash of nothing in exchange for a few
 * kilobytes.
 */

import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { SiteLayout, ScrollToTop } from './components/SiteChrome.jsx';
import { ToastHost, Spinner } from './components/ui.jsx';
import { useAuth } from './lib/stores.js';
import { useTracking } from './lib/tracking.js';
import {
  BlogIndex,
  CmsPage,
  NotFound,
  PostDetail,
  ProjectDetail,
  ProjectsIndex,
  ServiceDetail,
} from './pages/public/ContentPages.jsx';
import { Cart, Checkout, OrderPlaced, ProductDetail, Shop, TrackOrder } from './pages/public/Shop.jsx';

const AdminApp = lazy(() => import('./pages/admin/AdminApp.jsx'));

function FullPageSpinner() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <Spinner size={26} />
      <span className="sr-only">Loading</span>
    </div>
  );
}

/** The public site, with its header and footer. */
function PublicSite() {
  // Loads the Meta Pixel and analytics when they are configured, and reports route
  // changes. Mounted HERE rather than in `App` so the back office is never tracked:
  // staff clicking through the admin all day would swamp the numbers the ads are
  // judged on.
  useTracking();

  return (
    <SiteLayout>
      <ScrollToTop />
      <Routes>
        {/* Content-driven pages: same component, different slug. Adding a page in the
            back office and giving it a slug is enough to make it live at that address
            — see the catch-all at the bottom. */}
        <Route path="/" element={<CmsPage slug="home" />} />
        <Route path="/services" element={<CmsPage slug="services" />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/about" element={<CmsPage slug="about" />} />
        <Route path="/contact" element={<CmsPage slug="contact" />} />
        <Route path="/quote" element={<CmsPage slug="quote" />} />

        <Route path="/projects" element={<ProjectsIndex />} />
        <Route path="/projects/:slug" element={<ProjectDetail />} />

        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<PostDetail />} />

        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/:slug" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order/:orderNumber" element={<OrderPlaced />} />
        <Route path="/track" element={<TrackOrder />} />

        {/* Any other single segment is tried as a page slug. A page created in the back
            office is therefore reachable immediately, with no deploy — and `CmsPage`
            renders the not-found screen if there is no such page. */}
        <Route path="/:slug" element={<CmsSlugRoute />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </SiteLayout>
  );
}

/**
 * Resolve `/:slug` against the pages table.
 *
 * Reads the parameter from the ROUTER rather than from `window.location`. The two
 * agree in a browser, which is why the old version worked — but going around the router
 * meant this component could not render anywhere without a DOM, and it would disagree
 * with the router under any basename or a memory router.
 */
function CmsSlugRoute() {
  const { slug = '' } = useParams();
  return <CmsPage slug={slug} />;
}

export default function App() {
  const restore = useAuth((s) => s.restore);

  // Rehydrate the session once, on mount. The token in storage says nothing about
  // whether the account still exists, is active, or still has the role it claims —
  // only the server can answer that.
  useEffect(() => { void restore(); }, [restore]);

  return (
    <>
      <Routes>
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<FullPageSpinner />}>
              <AdminApp />
            </Suspense>
          }
        />
        {/* A stray `/administrator` or `/admin` without a trailing path lands on the
            dashboard rather than the public 404. */}
        <Route path="/administrator" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<PublicSite />} />
      </Routes>
      <ToastHost />
    </>
  );
}
